import React from 'react'
import useSize from './useSize'
import styled from 'styled-components'
import { timeDay } from 'd3-time'
import { scaleLinear } from 'd3-scale'
import { area, line, curveBasis } from 'd3-shape'
import { range } from 'd3-array'
import locale from 'd3-time-format/locale/de-CH.json'
import { timeFormat, timeFormatDefaultLocale } from 'd3-time-format'
timeFormatDefaultLocale(locale)

import fullyVacc from './fullyVacc.json'

const Svg = styled.svg`
  background-color: #fff;
`

const Axis = styled.line`
  stroke: #d4d4d4;
`

const Line = styled.line`
  stroke: #d4d4d4;
  stroke-dasharray: 2px 2px;
`

const AxisLabel = styled.text`
  font-size: 12px;
  fill: #7d7d7d;
`

const AreaChart = styled.path`
  fill: ${props => props.fill};
`

const LineChart = styled.path`
  stroke-width: 3px;
`

const xAxisFormat = timeFormat('%b')
const dateFormat = timeFormat('%-d. %B')
const dateFormatShort = timeFormat('%-d. %b')

export default function Timeline({
  vaccStart,
  vaccEnd,
}) {
  const containerRef = React.useRef(null)
  const size = useSize(containerRef)
  const margins = { top: 15, bottom: 15, left: 38 }
  const w = size.width
  const h = 300

  const phase1data = fullyVacc.map(d => ({ ...d, date: new Date(d.date) }))

  // console.log({ vaccWillingness, vaccStart, vaccEnd, vaccCapacity, vaccDays });

  const phase1x0 = phase1data[0].date
  const phase1Latest = phase1data[phase1data.length - 1]
  const phase2y0 = 100 - ((7095579 - 2227400) / 7095579) * 100
  const phase2Days = timeDay.range(timeDay.floor(vaccStart), vaccEnd)
  const phase2data = range(
    phase2y0,
    100,
    (100 - phase2y0) / phase2Days.length
  ).map((d, i) => ({
    value: d,
    date: phase2Days[i]
  }))

  const phaseGapDays = timeDay.range(
    timeDay.floor(phase1data[phase1data.length - 1].date),
    vaccStart
  )
  const yScaleGap = scaleLinear()
    .domain([0, phaseGapDays.length])
    .range([phase1Latest.value, phase2y0])

  const phaseGapData = phaseGapDays.map((d, i) => ({
    date: d,
    value: yScaleGap(i)
  }))

  const allData = phase1data.concat(phaseGapData).concat(phase2data)
  const highlights = [40, 60, 80].map(m => ({pct: m, val: allData.find(d => d.value >= m)}))

  const xScale = scaleLinear()
    .domain([phase1x0, new Date('2021-10-31')])
    .range([margins.left, w])

  const yScale = scaleLinear()
    .domain([0, 100])
    .range([h - margins.bottom, margins.top])

  const gapFn = line()
    .x(d => xScale(d.date))
    .y(d => yScale(d.value))

  const lineFn = line()
    .x(d => xScale(d.date))
    .y(d => yScale(d.value))

  const xTicks = timeDay.range(timeDay.floor(phase1x0), new Date('2021-10-31'))
  const yTicks = range(0, 120, 20)

  return (
    <div ref={containerRef}>
      <Svg width={size.width} height={h} viewBox={`0 0 ${size.width} ${h}`}>
        <defs>
          <linearGradient id='grad1' x1='0%' y1='100%' x2='0%' y2='0%'>
            <stop
              offset='0%'
              style={{ stopColor: 'rgb(44, 162, 159)', stopOpacity: 1 }}
            />
            <stop
              offset='100%'
              style={{ stopColor: 'rgb(202, 231, 231)', stopOpacity: 1 }}
            />
          </linearGradient>
        </defs>
        {xTicks.map(
          (d, i) =>
            d.getDate() == 1 && (
              <g>
                <Axis
                  x1={xScale(d)}
                  x2={xScale(d)}
                  y1={h - margins.top}
                  y2={h - margins.top + 4}
                ></Axis>
                <AxisLabel
                  x={xScale(timeDay.offset(d, 15))}
                  y={h - 2}
                  textAnchor='middle'
                >
                  {xAxisFormat(d)}
                </AxisLabel>
              </g>
            )
        )}
        <Axis
          x1={0 + margins.left}
          x2={0 + margins.left}
          y1={margins.top}
          y2={h - margins.bottom}
        />
        {yTicks.map(d => (
          <g>
            {d < 100 && (
              <Axis
                x1={0 + margins.left}
                x2={w + margins.left}
                y1={yScale(d)}
                y2={yScale(d)}
              />
            )}
            <AxisLabel x={margins.left-4} y={yScale(d) + 2} textAnchor='end'>{d}%</AxisLabel>
          </g>
        ))}
        <AxisLabel y={margins.top + 2} x={margins.left + 5}>
          mindestens einmal geimpft
        </AxisLabel>
        <Axis
          x1={0 + margins.left}
          x2={w + margins.left}
          y1={h - margins.bottom}
          y2={h - margins.bottom}
        />
        <g>
          <AxisLabel
            x={xScale(phase1Latest.date)}
            y={yScale(30)}
            textAnchor='middle'
          >
            {dateFormat(phase1Latest.date)}
          </AxisLabel>
          <Axis
            x1={xScale(phase1Latest.date)}
            x2={xScale(phase1Latest.date)}
            y1={yScale(phase1Latest.value) - 10}
            y2={yScale(27)}
          />
        </g>
        <LineChart d={lineFn(phase1data)} stroke='#E16D00' fill='none' />
        <LineChart
          d={gapFn(phaseGapData)}
          stroke='#7d7d7d'
          strokeDasharray='3,3'
        />
        <LineChart d={lineFn(phase2data)} stroke='#2CA29F' />
        <g>
          <AxisLabel x={xScale(vaccStart)} y={yScale(71)} textAnchor='middle'>
            Start
          </AxisLabel>
          <AxisLabel x={xScale(vaccStart)} y={yScale(71)+15} textAnchor='middle'>
            {dateFormat(vaccStart)}
          </AxisLabel>
          <Axis
            x1={xScale(vaccStart)}
            x2={xScale(vaccStart)}
            y1={yScale(phase2y0) - 10}
            y2={yScale(63)}
          />
        </g>
        <g
          transform={`translate(${xScale(
            phaseGapData.find((d, i) => i >= phaseGapData.length / 1.5).date
          )}, ${yScale(13)})`}
        >
          <AxisLabel>Für die Impfung der Risikogruppen</AxisLabel>
          <AxisLabel y={16}>
            verbleibende Zeit: {phaseGapData.length} Tage
          </AxisLabel>
        </g>
        {highlights.map(({pct,val}) => (
          <g transform={`translate(${xScale(val.date)},${yScale(pct)})`}>
            <circle r={4} fill={'#7d7d7d'} />
            <AxisLabel x={5} y={15}>{dateFormatShort(val.date)}</AxisLabel>
            <AxisLabel x={5} y={30}>{Math.round(val.value/10)*10}%</AxisLabel>
          </g>
        ))}
        {/* <Line x1={xScale(vaccEnd)} x2={xScale(vaccEnd)} y1={margins.top} y2={h-margins.bottom}></Line> */}
      </Svg>
    </div>
  )
}
